import {
  MsalAuthenticationTemplate,
  useIsAuthenticated,
  InteractionType,
} from 'src/auth/firebaseMsalCompat';
import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import { Footer } from '../components/Footer';
import { ScoreUploadBox } from '../components/ScoreUploadBox';
import { MAX_IMAGE_PAGES } from '../hooks/useFingeringUpload';
import { CreditSummary } from '../components/workspace/CreditSummary';
import { ProjectListItem } from '../components/workspace/ProjectListItem';
import { getDisplayName } from '../auth/displayName';
import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useProjectList, useProjectActions } from '../hooks/project';
import { Project } from '../types/project';
import { trackPageView, trackButtonClick } from '../services/telemetry';

const PROJECTS_PER_PAGE = 10;

function Workspace() {
  const isAuthenticated = useIsAuthenticated();
  const {
    projectList,
    statusLoading: isLoading,
    error,
    setProjectList,
  } = useProjectList(isAuthenticated);
  const { deleteProject } = useProjectActions(isAuthenticated);
  const { user } = useFirebaseAuth();
  const { profile, eligible } = useUserProfile();

  // A name that is just the email (web signups that skipped the username)
  // stays "My Projects". While the profile is loading, stay on "My Projects"
  // rather than flashing a Firebase name that may not be the saved one.
  const displayName = getDisplayName({
    profileName: profile?.name ?? null,
    firebaseUser: user,
    fallback: '',
    profilePending: eligible && !profile,
  });
  const email = (profile?.email ?? user?.email ?? '').trim();
  const nameIsEmail =
    !!email && displayName.trim().toLowerCase() === email.toLowerCase();
  const ownerFirstName =
    displayName && !nameIsEmail
      ? displayName.trim().split(/\s+/)[0]
      : undefined;
  const projectsHeading = ownerFirstName
    ? `${ownerFirstName}'s Projects`
    : 'My Projects';

  // Client-side search + pagination over the loaded project list.
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);

  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return projectList ?? [];
    return (projectList ?? []).filter((p: Project) =>
      p.name.toLowerCase().includes(q)
    );
  }, [projectList, searchQuery]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE)
  );
  const currentPage = Math.min(page, pageCount - 1);
  const pagedProjects = filteredProjects.slice(
    currentPage * PROJECTS_PER_PAGE,
    currentPage * PROJECTS_PER_PAGE + PROJECTS_PER_PAGE
  );

  // Reset to the first page whenever the search query changes.
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Fetch subscription status
  const {
    subscriptionStatus,
    statusLoading: subscriptionLoading,
    entitlement,
    retryStatus,
  } = useSubscriptionStatus(isAuthenticated);
  // Wait until both fetches have settled before deciding cap state — otherwise
  // the "Project limit reached" message can flash while subscription status is
  // still null.
  const isStatusReady =
    !isLoading && !subscriptionLoading && subscriptionStatus !== null;
  const hasExtraCredits = (subscriptionStatus?.extraCredits ?? 0) > 0;
  const isCreateProjectDisabled =
    isStatusReady &&
    subscriptionStatus?.status !== 'active' &&
    !hasExtraCredits &&
    (projectList?.length ?? 0) >= 3;

  // Only shrink the OMR bundle on a balance we actually read. Unknown is not
  // zero; the backend still applies the real demo limit.
  const creditsKnown = subscriptionStatus !== null;
  const totalCredits =
    (subscriptionStatus?.creditBalance ?? 0) +
    (subscriptionStatus?.extraCredits ?? 0);
  const imagePageLimit =
    !creditsKnown || totalCredits > 0 ? MAX_IMAGE_PAGES : 1;

  // Set the title of the page
  useEffect(() => {
    document.title = 'My Projects - PineappleMusicLab';
    trackPageView('Workspace', '/workspace');
  }, []);

  // Errors propagate to ProjectListItem, which tells the user.
  const handleDeleteProject = async (projectId: string) => {
    await deleteProject(projectId);
    setProjectList((prevProjects: Project[]) =>
      prevProjects.filter((project) => project._id !== projectId)
    );
  };

  const handleRenameProject = (updatedProject: Project) => {
    setProjectList((prevProjects: Project[]) =>
      prevProjects.map((project: Project) =>
        project._id === updatedProject._id
          ? { ...project, ...updatedProject } // Update all fields
          : project
      )
    );
  };

  const uploadBoxProps = {
    variant: 'member' as const,
    source: 'Workspace',
    imagePageLimit,
    disabled: isCreateProjectDisabled,
    disabledMessage: isCreateProjectDisabled
      ? 'You have reached the Free Plan project limit. Subscribe or delete a project to upload more.'
      : undefined,
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="flex-grow bg-[#fbfcfc] p-4 font-[Outfit]">
        <div className="mx-auto max-w-4xl">
          {/* Section 1: Create New Project — upload box + credit summary.
              The upload box mirrors the home-page one as a member flow (no
              guest soft cap; respects the project cap via `disabled`). */}
          <section className="mb-6 sm:mb-10 md:mb-20">
            <h1
              className={`
                mt-2 mb-6 text-[26px] font-light text-[#0d0d0d]
                sm:mt-4
                md:mt-6
              `}
            >
              Create New Project
            </h1>
            <div
              className={`grid grid-cols-1 items-stretch gap-4 md:grid-cols-2`}
            >
              {/* Compact (Home mobile) below md; dashed drag-drop box at md+. */}
              <div className="md:hidden">
                <ScoreUploadBox {...uploadBoxProps} layout="compact" />
              </div>
              <div className="hidden md:block">
                <ScoreUploadBox {...uploadBoxProps} layout="boxed" />
              </div>
              <CreditSummary
                subscriptionStatus={subscriptionStatus}
                entitlement={entitlement}
                onRetry={retryStatus}
              />
            </div>
          </section>

          {/* Section 2: My Projects — personalized heading, search, list. */}
          <section className="pb-16">
            <header className="mb-3 w-full sm:mb-4 md:mb-6">
              <h2 className="truncate text-[26px] font-light text-[#0d0d0d]">
                {projectsHeading}
              </h2>

              {/* Search */}
              <div className="relative mt-3 sm:mt-4 md:mt-6">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className={`
                    pointer-events-none absolute top-1/2 left-4 -translate-y-1/2
                    text-[#5d5d5d]
                  `}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  aria-label="Search projects"
                  className={`
                    w-full rounded-full bg-[rgba(236,237,237,0.6)] py-3 pr-4
                    pl-11 text-sm text-[#5d5d5d] placeholder-[#5d5d5d]
                    focus:bg-white focus:ring-2 focus:ring-yellow-400
                    focus:outline-none
                  `}
                />
              </div>
            </header>

            {/* Project List Container */}
            <div className="w-full max-w-4xl">
              {isLoading ? (
                <div className="text-center">Loading projects...</div>
              ) : error ? (
                <div className="text-center text-red-500">
                  Error loading projects: {error.message}
                  <button
                    onClick={() => {
                      trackButtonClick('Workspace', 'Retry Loading Projects');
                      window.location.reload();
                    }}
                    className={`
                      ml-2 cursor-pointer text-blue-500
                      hover:underline
                    `}
                  >
                    Retry
                  </button>
                </div>
              ) : projectList && projectList.length > 0 ? (
                filteredProjects.length > 0 ? (
                  <>
                    <div
                      className={`
                        flex items-center px-4 pb-2 text-xs font-normal
                        text-[#5d5d5d]
                      `}
                    >
                      <div className="min-w-0 flex-1 pr-3">Name</div>
                      <div
                        className={`
                          shrink-0 pr-2 whitespace-nowrap
                          sm:w-1/4 sm:min-w-[150px] sm:pr-4
                        `}
                      >
                        Last modified
                      </div>
                      <div
                        className={`
                          hidden w-1/4 min-w-[150px] shrink-0 pr-4
                          sm:block
                        `}
                      >
                        Created
                      </div>
                      {/* Spacer matching the 3-dot menu column width */}
                      <div className="ml-auto w-10 shrink-0"></div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {pagedProjects.map((project: Project) => (
                        <ProjectListItem
                          key={`${project._id}-${project.updatedAt}`}
                          project={project}
                          onDelete={handleDeleteProject}
                          onRename={handleRenameProject}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {pageCount > 1 && (
                      <div
                        className={`mt-6 flex items-center justify-center gap-4`}
                      >
                        <button
                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                          disabled={currentPage === 0}
                          aria-label="Previous page"
                          className={`
                            flex h-10 w-10 cursor-pointer items-center
                            justify-center rounded-full bg-gray-200
                            text-gray-600 transition-colors
                            hover:bg-gray-300
                            disabled:cursor-not-allowed disabled:opacity-40
                          `}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="text-sm text-gray-500">
                          {currentPage + 1} / {pageCount}
                        </span>
                        <button
                          onClick={() =>
                            setPage((p) => Math.min(pageCount - 1, p + 1))
                          }
                          disabled={currentPage >= pageCount - 1}
                          aria-label="Next page"
                          className={`
                            flex h-10 w-10 cursor-pointer items-center
                            justify-center rounded-full bg-gray-200
                            text-gray-600 transition-colors
                            hover:bg-gray-300
                            disabled:cursor-not-allowed disabled:opacity-40
                          `}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className={`
                      rounded-lg border border-gray-200 bg-white p-8 text-center
                      text-gray-500
                    `}
                  >
                    No projects match &quot;{searchQuery}&quot;.
                  </div>
                )
              ) : (
                <div
                  className={`
                    rounded-lg border border-gray-200 bg-white p-8 text-center
                    text-gray-500
                  `}
                >
                  You don&apos;t have any projects yet. Upload a score above to
                  get started.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export const WorkspaceWithMsal = () => {
  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <Workspace />
    </MsalAuthenticationTemplate>
  );
};
